import { cn } from '@/lib/utils';
import { Icons } from '@/libs/components/src/lib/icons';
import { AlertBlock } from '@chiroup/components/AlertBlock';
import { Button, ButtonColors } from '@chiroup/components/Button';
import { DatabaseTable } from '@chiroup/components/DatabaseTable';
import { Input } from '@chiroup/components/form/Input';
import { Icon } from '@chiroup/components/Icon';
import { Icons as SharedIcons } from '@chiroup/components/icons';
import { TimestampDisplay } from '@chiroup/components/TimestampDisplay';
import {
  BusinessRuleSeverity,
  STRING_ANY_HASH,
  STRING_BOOLEAN_HASH,
  STRING_NUMBER_HASH,
  STRING_STRING_HASH,
} from '@chiroup/core/constants/globals';
import { ChiroUpDayJsCommon } from '@chiroup/core/constants/stringConstants';
import { ChiroUpJSON } from '@chiroup/core/functions/ChiroUpJSON';
import { createDecimal } from '@chiroup/core/functions/createDecimal';
import { formatCurrency } from '@chiroup/core/functions/format';
import { ClinicLocation } from '@chiroup/core/types/Clinic.type';
import {
  EraPanelConfirmParams,
  EraPanelParams,
  EraPanelResponse,
} from '@chiroup/core/types/Devtools.type';
import { ClaimResponseEraStatus, ClaimResponseEraType } from '@chiroup/core/types/Era.type';
import { FormError } from '@chiroup/core/types/ErrorResponse.type';
import {
  CalendarDays as CalendarDaysIcon,
  Clipboard as ClipboardIcon,
  CreditCard as CreditCardIcon,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import useDevtools from '../../../hooks/useDevtools';

const ClassNames: any = {
  [ClaimResponseEraStatus.Posted]: 'bg-yellow-100',
  [ClaimResponseEraStatus.Applied]: 'bg-blue-100',
  [ClaimResponseEraStatus.Unapplied]: 'bg-red-100',
  [ClaimResponseEraStatus.Reprocessed]: 'bg-purple-100',
};

const defaultValues: EraPanelParams = {};

const EraPanel: React.FC = () => {
  const { get, isFetching, confirmed, preflightClaimResponses } = useDevtools('era-panel');
  const [getResponse, setGetResponse] = React.useState<EraPanelResponse | null>(null);
  const [confirmText, setConfirmText] = React.useState<string>('');
  const [storyId, setStoryId] = React.useState<string>('');
  const [copiedItemId, setCopiedItemId] = useState<string | number | null>(null);

  const {
    handleSubmit,
    control,
    formState: { errors },
    // setValue,
  } = useForm<EraPanelParams>({ defaultValues });

  const onSubmit = (data: EraPanelParams) => {
    setSelectedItemIds(() => ({}));

    if (get) {
      get(data)
        .then(res => {
          const obj = res as unknown as EraPanelResponse;
          setGetResponse(() => obj);

          return res;
        })
        .catch(e => undefined);
    } else {
    }
  };

  const resetInputs = () => {
    setConfirmText(() => '');
    setSelectedItemIds(() => ({}));
    setStoryId(() => '');
  };

  const handlePreflight = (data: EraPanelConfirmParams) => {
    if (preflightClaimResponses) {
      preflightClaimResponses(data)
        .then(res => {
          const obj = res as unknown as EraPanelResponse;

          if (obj) {
            setGetResponse(() => obj);
          }

          return res;
        })
        .catch(e => undefined);
    } else {
    }
  };

  const handleConfirmed = (data: EraPanelConfirmParams) => {
    if (confirmed) {
      confirmed(data)
        .then(res => {
          const obj = res as unknown as EraPanelResponse;

          if (obj) {
            setGetResponse(() => obj);
          }

          return res;
        })
        .catch(e => undefined)
        .finally(() => {
          resetInputs();
        });
    } else {
    }
  };

  const [selectedItemIds, setSelectedItemIds] = React.useState<STRING_BOOLEAN_HASH>({});

  const claimResponseEras = useMemo(() => {
    return getResponse?.items ?? [];
  }, [getResponse?.items]);

  const someArePosted = useMemo(() => {
    return true;

    // return claimResponseEras.some(item => item.status === ClaimResponseEraStatus.Posted);
  }, []);

  const purchases = useMemo(() => {
    return [];

    return getResponse?.purchases ?? [];
  }, [getResponse?.purchases]);

  const payments = useMemo(() => {
    return [];

    return getResponse?.payments ?? [];
  }, [getResponse?.payments]);

  const tasks = useMemo(() => {
    return getResponse?.tasks ?? [];
  }, [getResponse?.tasks]);

  const userTz = useMemo(() => {
    return getResponse?.clinic?.locations?.[0].timezone ?? ChiroUpDayJsCommon.defaultTimezone;
  }, [getResponse?.clinic?.locations]);

  const duplicateControlNumbers = useMemo(() => {
    const hash = claimResponseEras.reduce((acc, curr) => {
      const key = String(curr.controlNumber);

      if (acc[key]) {
        acc[key]++;
      } else {
        acc[key] = 1;
      }

      return acc;
    }, {} as STRING_NUMBER_HASH);

    return Object.keys(hash).filter(k => hash[k] > 1);
  }, [claimResponseEras]);

  const issuesBySeverity = useMemo(() => {
    const issues = getResponse?.issues ?? [];
    const hash = issues.reduce((acc, curr) => {
      const severity = curr.severity ?? BusinessRuleSeverity.Error;

      if (acc[severity]) {
        acc[severity].push(curr);
      } else {
        acc[severity] = [curr];
      }

      return acc;
    }, {} as STRING_ANY_HASH);

    return issues.length ? hash : null;
  }, [getResponse?.issues]);

  const responseErrors = useMemo(() => {
    return (
      (getResponse?.issues ?? []).filter(i => {
        return i.severity === BusinessRuleSeverity.Error;
      }) ?? []
    );
  }, [getResponse?.issues]);

  const era = useMemo(() => {
    return getResponse?.era ?? null;
  }, [getResponse?.era]);

  const payor = useMemo(() => era?.fullResponse?.header?.payor, [era]);
  const payee = useMemo(() => era?.fullResponse?.header?.payee, [era]);

  const clinic = useMemo(() => {
    return getResponse?.clinic ?? null;
  }, [getResponse?.clinic]);

  const exchanges = useMemo(() => {
    return getResponse?.exchanges ?? [];
  }, [getResponse?.exchanges]);

  const showOperations = useMemo(() => {
    return Object.values(selectedItemIds).some(v => v) && tasks.length === 0;
  }, [selectedItemIds, tasks.length]);

  useEffect(() => {
    if (copiedItemId !== null) {
      const timer = setTimeout(() => setCopiedItemId(null), 2000);

      return () => clearTimeout(timer);
    }
  }, [copiedItemId]);

  return (
    <div className="p-6">
      <form onSubmit={handleSubmit(onSubmit)}>
        <section className="bg-white border border-gray-400 inset-4 rounded-lg">
          <div className="p-4 flex flex-row justify-between">
            <h1 className="font-extrabold">ERA Panel</h1>
            <Button
              text="Fetch"
              disabled={isFetching}
              loading={isFetching}
              type="submit"
              icon={Icons.puzzle}
            />
          </div>
          <div className="p-4 m-4 border border-gray-400 bg-gray-50 rounded-lg text-sm">
            <h2 className="font-bold text-lg mb-2">Purpose</h2>
            <p className="mb-4">
              This shows all the information associated with an ERA. It can be used just to view the
              ERA or to perform a couple of operations on it.
            </p>
            <p>
              Sorry, the UI for this is basic. There are two buttons for the different operations.
              You have to preflight them and then confirm it before it happens.
            </p>
            <dl className="space-y-3">
              <div>
                <dt className="font-semibold text-blue-700">View</dt>
                <dd className="ml-4 text-gray-700">
                  View the ERA and its details. Boring, but it is all here including the full
                  document from the S3 bucket. There is no UI for that, but it could be written. You
                  can see/copy/etc. it on the browser console.
                </dd>
              </div>

              <div>
                <dt className="font-semibold text-blue-700">Delete</dt>
                <dd className="ml-4 text-gray-700">
                  Delete one or more claim responses from the ERA. This will delete the payments if
                  there are any posted. If all the claim responses are deleted, the ERA will be
                  deleted too.
                </dd>
              </div>

              <div>
                <dt className="font-semibold text-blue-700">Unpost</dt>
                <dd className="ml-4 text-gray-700">
                  This is the same as delete except instead of deleting the claim response row, it
                  just clears out the invoiceId, postedAt and sets the status to unapplied.
                </dd>
              </div>
            </dl>
          </div>
          <div className="p-4">
            <Controller
              name="eraId"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label="ERA id"
                  errors={
                    errors.eraId
                      ? ({
                          message: errors.eraId.message,
                        } as FormError)
                      : undefined
                  }
                />
              )}
            />
          </div>
        </section>
      </form>
      {issuesBySeverity ? (
        <section className="bg-white border border-gray-400 inset-4 rounded-lg mt-8 p-4">
          {/* <pre>{ChiroUpJSON.pretty(issuesBySeverity)}</pre> */}
          <IssuesBySeverity issues={issuesBySeverity} />
        </section>
      ) : null}
      {tasks.length > 0 && responseErrors.length === 0 ? (
        <section className="bg-white border border-gray-400 inset-4 rounded-lg mt-8 p-4">
          <h1 className="font-extrabold mb-4">Tasks</h1>
          <DatabaseTable
            clickable={false}
            schema={{
              table: {
                label: 'Table',
              },
              id: {
                label: 'ID',
              },
              billingKey: {
                label: 'Billing Key',
              },
              action: {
                label: 'Action',
              },
              type: {
                label: 'Type',
              },
              amount: {
                label: 'Amount',
              },
            }}
            instanceTitle={'Tasks'}
            rows={tasks}
            count={tasks.length}
          />

          <div className="flex flex-col space-y-4 px-4 mt-4">
            <Input
              label="Monday story number"
              placeholder="...story #..."
              name="storyId"
              value={storyId}
              onChange={e => setStoryId(() => e)}
            />
            <Input
              placeholder="...confirm..."
              name="confirmText"
              value={confirmText}
              onChange={e => setConfirmText(() => e)}
            />

            <div className="flex flex-row space-x-4 justify-end">
              <Button
                label="Cancel"
                color={ButtonColors.plainWithBorder}
                disabled={isFetching}
                onClick={() => {
                  resetInputs();
                  setGetResponse(() => null);
                }}
              />
              <Button
                label="Confirm Delete"
                loading={isFetching}
                disabled={isFetching || confirmText !== 'confirm' || storyId.length < 8}
                onClick={() =>
                  handleConfirmed({
                    ids: Object.keys(selectedItemIds).filter(k => selectedItemIds[k]),
                    tasks,
                    storyId,
                  })
                }
              />
              <Button
                label="Confirm Unpost"
                loading={isFetching}
                disabled={isFetching || confirmText !== 'confirm' || storyId.length < 8}
                onClick={() =>
                  handleConfirmed({
                    ids: Object.keys(selectedItemIds).filter(k => selectedItemIds[k]),
                    tasks,
                    storyId,
                    action: 'unpost',
                  })
                }
              />
            </div>
          </div>
          {/* <pre>{ChiroUpJSON.pretty(tasks)}</pre> */}
        </section>
      ) : null}
      {clinic ? (
        <section className="bg-white border border-gray-400 inset-4 rounded-lg mt-8 p-4">
          <div className="flex flex-row justify-between px-4 mt-4">
            <h2 className="text-base font-semibold leading-6 text-gray-900">
              Clinic #{clinic?.ID}
            </h2>
            <h2 className="flex">{clinic?.name}</h2>
          </div>
          <DatabaseTable
            instanceTitle={`Locations`}
            rows={clinic?.locations ?? []}
            count={clinic?.locations?.length ?? 0}
            clickable={false}
            schema={{
              name: {
                label: 'Name',
                td: (item: ClinicLocation) => {
                  return `${item?.name} (${item?.ID})`;
                },
              },
              city: {
                label: 'City',
              },
              state: {
                label: 'State',
              },
              phone: {
                label: 'Phone',
                td: (item: ClinicLocation) => {
                  return item?.phone ? item?.phone.replace(/[^0-9]/g, '') : '';
                },
              },
              fax: {
                label: 'Fax',
                td: (item: ClinicLocation) => {
                  return item?.fax ? item?.fax.replace(/[^0-9]/g, '') : '';
                },
              },
              address: {
                label: 'Address',
                td: (item: ClinicLocation) => {
                  return `${item?.address1} ${item?.address2}`;
                },
              },
            }}
          />
          {/* <pre>{ChiroUpJSON.pretty(clinic)}</pre> */}
        </section>
      ) : null}
      {era ? (
        <section className="bg-white border border-gray-400 inset-4 rounded-lg mt-8 p-4">
          <div className="flex flex-row justify-between">
            <h2 className="text-base font-semibold leading-6 text-gray-900">
              Electronic Remittance Advice ({era?.id})<small> # {era?.controlNumber}</small>
            </h2>
            <div className="rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-600 ring-1 ring-inset ring-green-600/20">
              {era?.status}
            </div>
          </div>
          {era?.verifiedBy ? (
            <div className="text-sm">
              {era?.verified ? 'Verified' : 'Unverified'} by <cite>{era?.verifiedBy}</cite>
              {era?.verified
                ? ` on ${ChiroUpDayJsCommon.display.datetimeWithTz(era?.verified, era?.tz, userTz)}`
                : ''}
            </div>
          ) : null}
          {!!era?.hasOrphanedClaims && (
            <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <div className="shrink-0"></div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    This ERA has orphaned claim response(s). Please allocate these response(s) to
                    existing claim(s).
                  </p>
                </div>
              </div>
            </div>
          )}
          <dt className="inline text-sm text-gray-900">
            <TimestampDisplay
              className="inline"
              ts={era?.createdAt}
              itemTz={era?.tz}
              userTz={userTz}
              prefix="Received: "
            />
          </dt>
          <dd className="inline text-sm text-gray-700"></dd>
          <br />
          <dt className="inline text-sm text-gray-900">
            <TimestampDisplay
              dateString={era?.financialDetails?.paymentEffectiveDate}
              prefix="Effective/Remittance date: "
            />
          </dt>{' '}
          <dd className="inline text-sm text-gray-700"></dd>
          <dl className="mt-6 grid grid-cols-1 text-sm leading-6 sm:grid-cols-2">
            <div className="sm:pr-4">
              <dt className="font-semibold text-gray-900">Payee</dt>
              <dd className="mt-2 text-gray-500">
                <span className="font-medium text-gray-900">{payee?.name}</span>
                <br />
                {`${payee?.address1 || ''} ${payee?.address2 || ''}`}
                <br />
                {`${payee?.city || ''} ${payee?.state || ''} ${payee?.postalCode || ''}`}
              </dd>
            </div>
            <dl className="flex flex-wrap">
              <div className="flex-auto">
                <dt className="text-sm leading-6 text-gray-900">Payment Amount</dt>
                <dd className="mt-1 text-base font-semibold leading-6 text-gray-900">
                  {formatCurrency(era?.financialDetails?.paymentAmount)}
                </dd>
              </div>

              <div className="mt-4 flex w-full flex-none gap-x-4 ">
                <dt className="flex-none">
                  <span className="sr-only">Payment Effective date</span>
                  <CalendarDaysIcon className="h-6 w-5 text-gray-400" aria-hidden="true" />
                </dt>
                <dd className="text-sm leading-6 text-gray-500">
                  {era?.financialDetails?.paymentEffectiveDate}
                </dd>
              </div>
              <div className="mt-4 flex w-full flex-none gap-x-4 mb-6">
                <dt className="flex-none">
                  <span className="sr-only">Status</span>
                  <CreditCardIcon className="h-6 w-5 text-gray-400" aria-hidden="true" />
                </dt>
                <dd className="text-sm leading-6 text-gray-500">
                  Paid via {era?.financialDetails?.paymentMethod}
                </dd>
              </div>
            </dl>
          </dl>
          <div className="px-4 py-8">
            <div className="pb-2 border-b border-gray-">
              <dt className="font-semibold text-gray-900">Payor</dt>
              <dd className="mt-2 text-gray-500">
                <span className="font-medium text-gray-900">
                  {payor?.name}
                  {` (${era?.payorId})`}
                </span>
                <br />
                {`${payor?.address1 || ''} ${payor?.address2 || ''}`}
                <br />
                {`${payor?.city || ''} ${payor?.state || ''} ${payor?.postalCode || ''}`}
              </dd>
            </div>
            <div className="mt-3">
              <div className="font-medium">Contact(s)</div>
              {payor?.contacts && payor?.contacts?.length > 0 ? (
                payor.contacts.map((contact, i) => {
                  return (
                    <div key={`contact-${i}`} className="mt-1">
                      <span className="text-gray-900">{contact.name}</span>
                      <br />
                      {contact.contactFunction && (
                        <span className="text-sm text-gray-500">{contact.contactFunction}</span>
                      )}
                      {contact.numbers.map((number, ni) => {
                        return (
                          <div key={`contact-${i}-${ni}`} className="text-sm text-gray-500">
                            {number.type}: {number.number}
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              ) : (
                <div>No contacts provided.</div>
              )}
            </div>
          </div>
        </section>
      ) : null}
      {exchanges.length > 0 ? (
        <section className="bg-white border border-gray-400 inset-4 rounded-lg mt-8 p-4">
          <DatabaseTable
            instanceTitle={`Exchanges`}
            rows={exchanges}
            options={
              {
                // defaultTdClassName: 'px-6 py-4 align-top text-sm font-light',
              }
            }
            clickable={false}
            schema={{
              id: {
                label: 'ID',
              },
              exchangeId: {
                label: 'Exchange Detail',
                tdClassName: ' align-top text-xs font-light',
                td: (item: any) => {
                  const renderFieldWithCopy = (
                    label: string,
                    value: string | null | undefined,
                    fieldKey: string
                  ) => {
                    const isCopied = copiedItemId === `${item?.id}-${fieldKey}`;
                    const hasValue = !!value;

                    return (
                      <div className="flex items-center gap-2">
                        <div className="w-32 font-semibold">{label}:</div>
                        <div className={cn(hasValue ? '' : 'italic', 'flex-1')}>
                          {value ?? '-na-'}
                        </div>
                        {hasValue && (
                          <>
                            {/* shut up linter! */}
                            {isCopied ? (
                              <Icon
                                icon={SharedIcons.check}
                                className="h-4 w-4 text-green-500 flex-shrink-0"
                              />
                            ) : (
                              <button
                                type="button"
                                className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-pointer flex-shrink-0"
                                onClick={() => {
                                  navigator.clipboard.writeText(value).then(
                                    () => {
                                      setCopiedItemId(`${item?.id}-${fieldKey}`);
                                    },
                                    err => {}
                                  );
                                }}
                                title={`Copy ${label.toLowerCase()} to clipboard`}
                              >
                                <ClipboardIcon className="h-4 w-4" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    );
                  };

                  return (
                    <div className="flex flex-col gap-1">
                      {renderFieldWithCopy('ID', item?.exchangeId, 'exchangeId')}
                      {renderFieldWithCopy('Type', item?.exchangeType, 'exchangeType')}
                      {renderFieldWithCopy('Source', item?.source, 'source')}
                      {renderFieldWithCopy('Target Path', item?.targetPath, 'targetPath')}
                      {renderFieldWithCopy('Source Path', item?.sourcePath, 'sourcePath')}
                    </div>
                  );
                },
              },
              touched: {
                label: 'Touched',
                td: (item: any) => {
                  return <TimestampDisplay ts={item?.touched} itemTz={item.tz} userTz={userTz} />;
                },
              },
              content: {
                label: 'File',
                td: (item: any) => {
                  if (!item?.content) {
                    return <span>-- no content --</span>;
                  }

                  const isCopied = copiedItemId === item?.id;

                  return (
                    <div className="flex items-center gap-2">
                      <span>{(item?.content ?? '').substring(0, 100) + '...'}</span>
                      {isCopied ? (
                        <Icon icon={SharedIcons.check} className="h-5 w-5 text-green-500" />
                      ) : (
                        <button
                          type="button"
                          className="h-5 w-5 text-gray-400 hover:text-gray-600 cursor-pointer"
                          onClick={() => {
                            navigator.clipboard.writeText(item?.content).then(
                              () => {
                                setCopiedItemId(item?.id);
                              },
                              err => {}
                            );
                          }}
                          title="Copy full content to clipboard"
                        >
                          <ClipboardIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  );
                },
              },
            }}
            count={exchanges.length}
          />
          {/* <pre>{ChiroUpJSON.pretty(exchanges)}</pre> */}
        </section>
      ) : null}
      {getResponse && claimResponseEras.length > 0 ? (
        <section className="bg-white border border-gray-400 inset-4 rounded-lg mt-8 p-4">
          <h1 className="font-extrabold">ERA #{getResponse?.era?.id}</h1>
          {duplicateControlNumbers.length > 0 && (
            <AlertBlock
              level={`none`}
              message={`The following control numbers are duplicated on this ERA: ${duplicateControlNumbers.join(
                ', '
              )}.`}
            />
          )}
          {claimResponseEras.length > 0 ? (
            <DatabaseTable
              columnMetadata={{
                action: {
                  content:
                    tasks.length !== 0 || !someArePosted ? null : (
                      <div
                        className="hover:text-primary-600 cursor-pointer flex flex-row w-12 select-none text-gray-300 text-xs"
                        onClick={() => {
                          setSelectedItemIds(prev => {
                            const newSelectedItemIds = { ...prev };
                            claimResponseEras.forEach(item => {
                              newSelectedItemIds[item.id] = true;
                            });

                            return newSelectedItemIds;
                          });
                        }}
                        onDoubleClick={() => {
                          setSelectedItemIds(prev => {
                            const newSelectedItemIds = { ...prev };
                            Object.keys(newSelectedItemIds).forEach(key => {
                              newSelectedItemIds[key] = false;
                            });

                            return newSelectedItemIds;
                          });
                        }}
                      >
                        <Icon icon={SharedIcons.checkCircle} className="w-8 h-8" />
                      </div>
                    ),
                },
              }}
              instanceTitle={`Invoices`}
              rows={claimResponseEras}
              clickable={false}
              schema={{
                controlNumber: {
                  label: 'Control #s',
                  showOnList: true,
                  td: (item: ClaimResponseEraType) => {
                    return (
                      <div className="flex justify-start flex-row w-32">
                        <div className="flex grow">
                          {item.controlNumber}
                          <span
                            className={cn(
                              'rounded-lg pt-1 px-2 mt-1 text-[10px] leading-[10px] font-light text-gray-600 ml-2 whitespace-nowrap h-5',
                              ClassNames[item.status]
                            )}
                          >
                            {item.status}
                          </span>
                        </div>
                        <div className="text-gray-400 text-xs pl-4 flo">({item?.id})</div>
                      </div>
                    );
                  },
                  thClassName: 'text-sm',
                },
                patientName: {
                  label: 'Patient',
                  showOnList: true,
                  tdClassName: 'min-w-56',
                  td: (item: ClaimResponseEraType) => {
                    return <div>{item.patientName}</div>;
                  },
                  thClassName: 'text-sm',
                },
                services: {
                  label: 'Services',
                  tdClassName: 'whitespace-nowrap',
                  td: (item: ClaimResponseEraType) => {
                    return (
                      <div className="flex flex-col">
                        {item?.serviceItems?.length > 0 ? (
                          item.serviceItems.map((line, i) => {
                            return (
                              <div key={`line-${i}`}>
                                {line?.adjudicatedServiceCode} -{' '}
                                {createDecimal(line?.remitAmount ?? 0)
                                  .toNumber()
                                  .toFixed(2)}
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-gray-400">No services</div>
                        )}
                      </div>
                    );
                  },
                },
                processedStatus: {
                  label: 'Status',
                  showOnList: true,
                  td: (item: ClaimResponseEraType) => {
                    return (
                      <div className="flex flex-col">
                        <div className="w-full flex justify-start">
                          {item.fullClaimResponse?.statusCode
                            ? `${item.fullClaimResponse?.statusCode} - ${item.fullClaimResponse?.statusDescription}`
                            : '-na-'}
                        </div>
                        <TimestampDisplay
                          ts={item.createdAt}
                          itemTz={item.tz}
                          userTz={userTz}
                          prefix="Received: "
                        />
                        <TimestampDisplay
                          ts={item.postedAt}
                          itemTz={item.tz}
                          userTz={userTz}
                          errorValue=""
                          prefix="Posted: "
                        />
                      </div>
                    );
                  },
                },
                remittanceAmount: {
                  label: 'Amount',
                  type: 'currency',
                  showOnList: true,
                  td: (item: ClaimResponseEraType) => {
                    return formatCurrency(item.paymentAmount);
                  },
                },
                action: {
                  label: '',
                  td: (item: ClaimResponseEraType) => {
                    return tasks.length !== 0 || !someArePosted ? null : (
                      <div
                        className={cn(
                          'cursor-pointer h-8 w-8',
                          selectedItemIds[item.id] ? 'text-primary-600' : 'text-gray-400'
                        )}
                        onClick={() =>
                          setSelectedItemIds(prev => ({
                            ...prev,
                            [item.id]: !prev[item.id],
                          }))
                        }
                      >
                        <Icon icon={SharedIcons.checkCircle} />
                      </div>
                    );
                  },
                },
              }}
              cellClassName={`text-sm text-gray-900`}
              count={claimResponseEras.length}
            />
          ) : (
            <AlertBlock level={`none`} message={`This ERA has no claim responses.`} />
          )}
          {showOperations ? (
            <div className="flex justify-end px-3 pt-4 space-x-4">
              <Button
                label="Preflight Delete"
                loading={isFetching}
                disabled={isFetching}
                onClick={() =>
                  handlePreflight({
                    ids: Object.keys(selectedItemIds).filter(k => selectedItemIds[k]),
                    action: 'delete',
                  })
                }
              />
              <Button
                label="Preflight Unpost"
                loading={isFetching}
                disabled={isFetching}
                onClick={() =>
                  handlePreflight({
                    ids: Object.keys(selectedItemIds).filter(k => selectedItemIds[k]),
                    action: 'unpost',
                  })
                }
              />
            </div>
          ) : null}
        </section>
      ) : null}
      {purchases.length > 0 ? (
        <section className="bg-white border border-gray-400 inset-4 rounded-lg mt-8 p-4">
          <h1 className="font-extrabold mb-4">Purchases</h1>
          <pre>{ChiroUpJSON.pretty(purchases)}</pre>
        </section>
      ) : null}

      {payments.length > 0 ? (
        <section className="bg-white border border-gray-400 inset-4 rounded-lg mt-8 p-4">
          <h1 className="font-extrabold mb-4">Payments</h1>
          <pre>{ChiroUpJSON.pretty(payments)}</pre>
        </section>
      ) : null}
    </div>
  );
};

const IssuesBySeverity: React.FC<{ issues: STRING_ANY_HASH | null }> = ({ issues }) => {
  const severityToLabel: STRING_STRING_HASH = {
    [BusinessRuleSeverity.Error]: 'Errors',
    [BusinessRuleSeverity.Warning]: 'Warnings',
    [BusinessRuleSeverity.Info]: 'Informational Messages',
  };

  if (!issues) {
    return null;
  }

  return (
    <div className="flex flex-col space-y-4 px-4">
      {Object.entries(issues).map(([severity, issueList]) => (
        <div key={severity} className="flex flex-col">
          <h3 className="font-semibold text-md">{severityToLabel[severity]}</h3>
          <ul className="ml-4 list-disc list-inside">
            {issueList.map((issue: any, index: number) => (
              <li key={index} className="text-sm">
                {issue.text || 'No text provided'}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

export default EraPanel;
